/**
 * English UI copy. Shape is checked against `Dictionary` (see ru.ts), so a
 * missing or misspelled key fails the build rather than rendering blank.
 *
 * `app.title` and `app.subtitle` are the product's own wording and stay
 * identical in every language; so do the satellite names and the language
 * names in `languages`.
 */
import { forms } from "./plural";
import type { Dictionary } from "./ru";

export const en: Dictionary = {
  app: {
    title: "KGS Methane Monitoring",
    subtitle: "Satellite-based methane detection system",
    demoBadge: "DEMO",
    demoTitle: "Synthetic demo data, not real measurements",
  },
  header: {
    themeToggleToLight: "Switch to light theme",
    themeToggleToDark: "Switch to dark theme",
    languageLabel: "Interface language",
  },
  languages: {
    ru: "Русский",
    kk: "Қазақша",
    en: "English",
  },
  stats: {
    detections: "Detections",
    emissions: "kg/h",
    countLabel: "Count:",
    modeExtent: "in current view",
    modeCountry: "everywhere",
    modeExtentHint: "Counts only detections inside the visible map rectangle",
    modeCountryHint: "Counts every detection across the monitored area, filters applied",
  },
  basemap: {
    positron: "Map (light)",
    darkMatter: "Map (dark)",
    satellite: "Satellite",
    satelliteLabels: "Satellite + labels",
    osm: "OSM standard",
    topo: "Terrain",
  },
  map: {
    resetExtent: "Full extent",
    fullscreen: "Fullscreen",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    basemap: "Base map",
    region: "Region",
    cursorPosition: "Cursor coordinates",
    copyCoordinates: "Copy coordinates",
    coordinatesCopied: "Copied",
  },
  layers: {
    title: "Layers",
    open: "Show layers panel",
    collapse: "Collapse",
    groupBasemap: "Base map",
    groupBorders: "Borders",
    groupData: "Monitoring data",
    country: "Country borders",
    regions: "Province borders",
    plumes: "Plume polygons",
    raster: "Raster concentration",
    rasterOpacity: "Raster opacity",
    rasterPending: "The raster appears once an episode is selected",
    sources: "Point sources",
    aoi: "Areas of interest",
  },
  filters: {
    title: "Filters",
    open: "Show filters",
    collapse: "Collapse",
    period: "Period",
    presetD7: "7 days",
    presetD30: "30 days",
    presetD90: "90 days",
    presetY1: "year",
    presetAll: "all time",
    satellites: "Satellites",
    minRate: "Minimum flow rate, kg/h",
    apply: "Apply",
    reset: "Reset",
    export: "Export data",
    exportSoon: "Data export arrives in stage 7",
    chipPeriod: "Period",
    chipMinRate: "Min. rate",
    removeFilter: "Remove filter",
    changesPlural: forms({
      one: "{n} change",
      other: "{n} changes",
    }),
  },
  satelliteNames: {
    "sentinel-2": "Sentinel-2",
    "landsat-8": "Landsat 8",
    "landsat-9": "Landsat 9",
    emit: "EMIT",
  },
  states: {
    loading: "Loading data…",
    emptyTitle: "No detections found",
    emptyBody:
      "Nothing matches the current filters. Widen the period or lower the minimum flow rate.",
    emptyReset: "Reset filters",
    errorTitle: "Something went wrong",
    errorBody: "Could not load monitoring data.",
    errorRetry: "Reload the page",
  },
  export: {
    title: "Export data",
    format: "Format",
    area: "Area",
    areaExtent: "Current extent",
    areaCountry: "Full extent",
    fields: "Field set",
    fieldsAll: "All fields",
    fieldsBasic: "Essentials only",
    submit: "Download",
    cancel: "Cancel",
    close: "Close",
    exporting: "Preparing…",
    empty: "No data to export under the current filters",
    done: "Rows exported:",
    error: "Export failed (run npm install for XLSX)",
  },
  detail: {
    episode: "Episode",
    close: "Close",
    durationDays: "days",
    detections: "detections",
    obsDates: "acquisition dates",
    maxRate: "max kg/h",
    silenceCaveat:
      "A gap between detections does not mean the emission stopped: the satellite may simply not have observed the source on those days, or the signal was suppressed.",
    attributes: "Attributes",
    sensors: "Sensors",
    extent: "Plume extent",
    totalMass: "Total mass",
    notCalculated: "not calculated",
    coordinates: "Source coordinates",
    source: "Source",
    sector: "Sector",
    operator: "Operator",
    region: "Province",
    noSource: "unassigned",
    chartTitle: "Flow rate over time",
    chartEmpty: "No quantified estimates to chart",
    rasterTitle: "Raster preview",
    rasterEmpty: "Raster preview unavailable",
    tableTitle: "Detections",
    colDate: "Date",
    colRate: "Rate",
    colConfidence: "Conf.",
    colSatellite: "Satellite",
    download: "Download episode data",
    downloadSoon: "Episode export arrives in stage 7",
    km: "km",
    kg: "kg",
    kgh: "kg/h",
    notFound: "Episode not found",
    unquantified: "not quantified",
    noWind: "no wind",
    failed: "failed",
    meanRate: "Mean flow rate",
    gapDays: "Clustering window",
    mergedWarn: "The extent is large — separate sources may have been merged.",
    sourceEpisodes: "Episodes at this source",
    noEpisodes: "No episodes on record",
  },
  sectorNames: {
    oil_gas: "Oil and gas",
    coal_mine: "Coal",
    coal: "Coal",
    landfill: "Waste",
    waste: "Waste",
    agriculture: "Agriculture",
    other: "Other",
  },
  timeline: {
    play: "Play",
    pause: "Pause",
    speed: "Speed",
    cursor: "Current date",
    window: "Gap",
    windowUnit: "d",
    windowHint:
      "How many days back from the current date a detection still counts as active. Opacity encodes the age of a detection (time), not the strength of the emission.",
    noDates: "No detections in the selected period",
    activeEpisodes: forms({
      one: "{n} active episode",
      other: "{n} active episodes",
    }),
  },
  legend: {
    title: "Flow rate scale",
    open: "Show legend",
    collapse: "Collapse legend",
    low: "under 500 kg/h",
    moderate: "500 – 1,000 kg/h",
    high: "1,000 – 2,000 kg/h",
    severe: "over 2,000 kg/h",
    unknown: "not quantified",
    filterHint: "Click a row to show only that category",
    showAll: "Show all",
  },
};
